import React, { useState } from 'react';
import * as yup from 'yup';
import { FormikHelpers } from 'formik/dist/types';
import { reqString, } from 'components/forms/formValidations';
import XTextInput from 'components/inputs/XTextInput';
import { Alert, Box, Card, CardContent, Divider, Grid, Link, Typography } from '@mui/material';
import ListViewLayout from '../../../../../components/layout/ListViewLayout';
import { localRoutes, remoteRoutes } from '../../../../../constants';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { hasValue } from '../../../../../components/inputs/inputHelpers';
import { getData } from '../../../../../utils/fetchHelpers';
import Loading from '../../../../../components/loaders/Loading';
import { useQuery, useQueryClient } from 'react-query';
import ErrorComponent from '../../../../../components/ErrorComponent';
import XTextAreaInput from '../../../../../components/inputs/XTextAreaInput';
import TemplateTable from './components/TemplateTable';
import {
  validateCaptureBankAccount,
  validateCaptureEmail,
  validateCaptureFace,
  validateCaptureMobileMoneyNumber,
  validateCaptureNationalId,
  validateCapturePhoneNumber,
  validateCapturePhysicalAddress,
  validateCompletionEmailBody,
  validateInvitationEmailBody
} from './config';
import { biometricsTable, contactsTable, financialTable, idDocumentsTable } from './components/config';
import { printMoney } from '../../../../../utils/numberHelpers';
import { post, put } from '../../../../../utils/ajax';
import Toast from '../../../../../utils/Toast';
import { OnboardingActivity, OnboardingTemplateViewModel } from '../../../../../__generated__/kycTypes';
import ActivationManagement from 'pages/account/ActivationManagement';


const schema = yup.object().shape(
  {
    name: reqString,
    description: reqString,

    captureNationalId: validateCaptureNationalId,
    verifyNationalId: null,

    captureFace: validateCaptureFace,
    verifyFace: null,

    capturePhoneNumber: validateCapturePhoneNumber,
    verifyPhoneNumber: null,

    captureEmailAddress: validateCaptureEmail,
    verifyEmailAddress: null,

    capturePhysicalAddress: validateCapturePhysicalAddress,

    captureMobileMoneyNumber: validateCaptureMobileMoneyNumber,
    verifyMobileMoneyNumber: null,

    captureBankAccount: validateCaptureBankAccount,

    invitationEmailBody: validateInvitationEmailBody,
    completionEmailBody: validateCompletionEmailBody,
  }
);

const PeopleTemplateForm = ({ data, cacheKey, baseCharge }: {
  data: OnboardingTemplateViewModel,
  cacheKey: string[],
  baseCharge: number
}) => {
  const isEditMode = hasValue(data);
 

  const navigate = useNavigate();
  const client = useQueryClient();
  const [minimumCost, setMinimumCost] = useState(data?.minimumTemplateCharge > 0 ? data?.minimumTemplateCharge : 0);
  const [maximumCost, setMaximumCost] = useState(data?.maximumTemplateCharge > 0 ? data?.maximumTemplateCharge : baseCharge);

  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(() => {
    const categories = new Set<string>();

    const hasIdVerification = data?.activities?.some(
      activity => activity?.name === OnboardingActivity.CaptureNationalId.toString() && activity?.shouldVerify === true
    );


    if (isEditMode && hasIdVerification) {
      categories.add('IdVerification');
    }

    const hasPhoneVerification = data?.activities?.some(
      activity => activity?.name === OnboardingActivity.CapturePhoneNumber.toString() && activity?.shouldVerify === true);

    const hasMobileMoneyVerification = data?.activities?.some(
      activity => activity?.name === OnboardingActivity.CaptureMobileMoneyNumber.toString() && activity?.shouldVerify === true);
    

    if (isEditMode && hasPhoneVerification) {
      categories.add('PhoneVerification');
    }

    if (isEditMode && hasMobileMoneyVerification) {
      categories.add('Other');
    }

    return categories;
  });


  function loadTemplatePropertiesFromActivities(activities, initialValues) {

    const map = {
      CaptureNationalId: 'NationalId',
      CaptureFace: 'Face',
      CaptureEmailAddress: 'EmailAddress',
      CapturePhoneNumber: 'PhoneNumber',
      CapturePhysicalAddress: 'PhysicalAddress',
      CaptureMobileMoneyNumber: 'MobileMoneyNumber',
      CaptureBankAccount: 'BankAccount',
      CaptureProofOfAddress: 'ProofOfAddress'
    };

    activities.forEach(activity => {
      const keySuffix = map[activity.name];
      if (!keySuffix) return;

      initialValues[`capture${keySuffix}`] = true;
      initialValues[`verify${keySuffix}`] = activity.shouldVerify || false;
      initialValues[`require${keySuffix}`] =
        activity.fields?.some(field => field.isRequired) || false;

      // special handling: link PhysicalAddress and ProofOfAddress
      if (activity.name === 'CapturePhysicalAddress' || activity.name === 'CaptureProofOfAddress') {
        ['PhysicalAddress', 'ProofOfAddress'].forEach(suffix => {
          initialValues[`capture${suffix}`] = true;
          initialValues[`verify${suffix}`] = activity.shouldVerify || false;
          initialValues[`require${suffix}`] = activity.shouldVerify || false;
        });
      }
      // special handling: Face
      if (activity.name === 'CaptureFace') {
        initialValues.requireFace = activity.shouldVerify || false;
      }

    });
    // this field is disabled by default
    initialValues.verifyPhysicalAddress = false;
    return initialValues;
  }

  const initialValues = isEditMode ?
    { ...data } : {
      name: null,
      description: null,

      captureNationalId: null,
      verifyNationalId: null,
      requireNationalId: null,

      captureFace: null,
      verifyFace: null,
      requireFace: null,

      capturePhoneNumber: null,
      verifyPhoneNumber: null,
      requirePhoneNumber: null,

      captureEmailAddress: null,
      verifyEmailAddress: null,
      requireEmailAddress: null,

      capturePhysicalAddress: null,
      verifyPhysicalAddress: null,
      requirePhysicalAddress: null,

      captureMobileMoneyNumber: null,
      verifyMobileMoneyNumber: null,
      requireMobileMoneyNumber: null,

      captureBankAccount: null,
      verifyBankAccount: null,
      requireBankAccount: null,

      invitationEmailSubject: null,
      invitationEmailBody: null,

      completionEmailSubject: null,
      completionEmailBody: null,

      invitationSms: null,
      completionSms: null
    };
  if (isEditMode) {
    loadTemplatePropertiesFromActivities(data.activities, initialValues);
  }

  const handleSubmit = (values: any, actions: FormikHelpers<any>) => {
    actions.setSubmitting(true);

    const request = {
      name: values.name,
      description: values.description,
      captureNationalId: values.captureNationalId ?? false,
      verifyNationalId: values.verifyNationalId ?? false,
      requireNationalId: values.requireNationalId ?? false,
      captureFace: values.captureFace ?? false,
      verifyFace: values.verifyFace ?? false,
      requireFace: values.requireFace ?? false,
      capturePhoneNumber: values.capturePhoneNumber ?? false,
      verifyPhoneNumber: values.verifyPhoneNumber ?? false,
      requirePhoneNumber: values.requirePhoneNumber ?? false,
      captureEmailAddress: values.captureEmailAddress ?? false,
      verifyEmailAddress: values.verifyEmailAddress ?? false,
      requireEmailAddress: values.requireEmailAddress ?? false,
      capturePhysicalAddress: values.capturePhysicalAddress ?? false,
      verifyPhysicalAddress: values.verifyPhysicalAddress ?? false,
      requirePhysicalAddress: values.requirePhysicalAddress ?? false,
      captureMobileMoneyNumber: values.captureMobileMoneyNumber ?? false,
      verifyMobileMoneyNumber: values.verifyMobileMoneyNumber ?? false,
      requireMobileMoneyNumber: values.requireMobileMoneyNumber ?? false,
      captureBankAccount: values.captureBankAccount ?? false,
      verifyBankAccount: values.verifyBankAccount ?? false,
      requireBankAccount: values.requireBankAccount ?? false,
      invitationEmailSubject: values.invitationEmailSubject,
      invitationEmailBody: values.invitationEmailBody,
      completionEmailSubject: values.completionEmailSubject,
      completionEmailBody: values.completionEmailBody,
      invitationSms: values.invitationSms,
      completionSms: values.completionSms
    };

    if (isEditMode) {
      put(remoteRoutes.OnboardingTemplates, { id: data.id, ...request },
        resp => {
          console.log(resp);
          Toast.success('Template updated successfully');
          client.invalidateQueries(cacheKey).then(
            () => {
              navigate(localRoutes.onboardingTemplates);

            }
          );
        },
        undefined,
        () => {
          actions.setSubmitting(false);
        }
      );
    } else {
      post(remoteRoutes.OnboardingTemplates, request,
        resp => {
          console.log(resp);
          Toast.success('Template created successfully');
          client.invalidateQueries(cacheKey).then(
            () => {
              navigate(localRoutes.onboardingTemplates);
            }
          );
        },
        undefined,
        () => {
          actions.setSubmitting(false);
        }
      );
    }
  };

  const handleCheckboxChange = async (fieldName: string, isChecked: boolean) => {
    console.log('Field:', fieldName, '| Checked:', isChecked);

    const fieldToCategoryMap: Record<string, string> = {
      verifyPhoneNumber: 'PhoneVerification',
      verifyMobileMoneyNumber: 'Other',
      verifyNationalId: 'IdVerification',
    };

    const productCategory = fieldToCategoryMap[fieldName];

    if (!productCategory) {
      console.warn('No product category mapped for field:', fieldName);
      return;
    }
    const updated = new Set(selectedCategories);

    if (isChecked) {
      if (!updated.has(productCategory)) {
        console.log(`Adding "${productCategory}"`);
        updated.add(productCategory);
      } else {
        console.log(`Category "${productCategory}" already selected, skipping.`);
      }
    } else {
      console.log(`Deleting "${productCategory}"`);
      updated.delete(productCategory);
    }

    console.log('updated categories: ', updated);
    setSelectedCategories(updated);

    const params = new URLSearchParams();
    updated.forEach((category: string) => {
      params.append('ProductCategories', category);
    });

    const url = `${remoteRoutes.peopleOnboardingTemplatesChargeEstimate}?${params.toString()}`;
    console.log('Generated URL:', url);
    try {
      const response = await getData(url); // Make sure getData returns a Promise

      console.log('Min Total Price:', response.minTotalPrice);
      console.log('Max Total Price:', response.maxTotalPrice);
      setMinimumCost(response.minTotalPrice);
      setMaximumCost(response.maxTotalPrice);
    } catch (error) {
      console.error('Error fetching charge estimate:', error);
    }

  };

  const handleRefresh = async () => {
    client.invalidateQueries(cacheKey).then(
      () => {
        navigate(localRoutes.onboardingTemplates);
      }
    );
  };

  const activationWarning = (
    <Box>
      <span>
        <Typography variant="body2">
          You are about to activate this template. If you proceed, your Users will be able to access your onboarding workflow.
        </Typography>
      </span>
      <span>
        <Typography variant="body2">
          If you would like to proceed with activation, click "activate" below. Otherwise, click cancel to abort the action
        </Typography>
      </span>
    </Box>
  );

  const deactivationWarning = (
    <Box>
      <span>
        <Typography variant="body2">
          You are about to deactivate this template. If you proceed, your Users will not be able to access your onboarding workflow.
        </Typography>
      </span>
      <span>
        <Typography variant="body2">
          If you would like to proceed with deactivation, click "Deactivate" below. Otherwise, click cancel to abort the action
        </Typography>
      </span>
    </Box>
  );

  return (
    <Grid container>
      <Grid item
        xs={12}>
        <Box height="100%">
          <ListViewLayout
            title={isEditMode ? 'Onboarding Template Details' : 'New Onboarding Template'}
            crumbs={[
              { title: 'Products', path: localRoutes.onboardingTemplates },
              { title: 'Onboarding', path: localRoutes.onboardingTemplates },
              { title: 'Templates', path: localRoutes.onboardingTemplates },
              { title: hasValue(data) ? 'Details' : 'New', path: '' },
            ]}
          >
            <Grid item
              xs={12}
              mb={1}>
              <Card>
                <CardContent>
                  <Box>
                    <Formik
                      initialValues={initialValues}
                      onSubmit={handleSubmit}
                      validationSchema={schema}
                      validateOnBlur
                      enableReinitialize={false}
                    >
                      {({ submitForm, isSubmitting, values }) => {

                        return (
                          <Box>
                            <Grid
                              container
                              sx={{
                                pt: 2
                              }}
                            >
                              <Grid item
                                xs={12}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'end',
                                  }}
                                >
                                 {minimumCost === 0 ? (
                                    <>
                                      <Typography 
                                        variant="h6" 
                                        sx={{ m: 2 }}
                                      >
                                        Price per customer onboarded:
                                      </Typography>
                                      <Typography 
                                        variant="h6" 
                                        color="primary" 
                                        sx={{ m: 2, ml: 1, mr: 3 }}
                                      >
                                        {printMoney(maximumCost)}
                                      </Typography>
                                    </>
                                 ) : (
                                    <>
                                      <Typography 
                                        variant="h6" 
                                        sx={{ m: 2 }}
                                      >
                                        Your onboarding process will cost between:
                                      </Typography>
                                      <Typography 
                                        variant="h6" 
                                        color="primary" 
                                        sx={{ m: 2, ml: 1, mr: 3 }}
                                      >
                                        {printMoney(minimumCost)} – {printMoney(maximumCost)}
                                      </Typography>
                                    </>
                                 )}
 
                                </Box>

                                <Divider />
                              </Grid>
                            </Grid>
                            <Form>
                              <Grid
                                container
                                spacing={3}
                                sx={{ p: 5 }}
                              >

                                <Grid item
                                  xs={12}
                                >
                                  <Grid item
                                    xs={12}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      NAME
                                    </Typography>
                                    <XTextInput
                                      name="name"
                                      label=""
                                      variant='outlined'
                                    />
                                  </Grid>
                                  <Grid item
                                    xs={12}
                                    sx={{ mt: 2 }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      DESCRIPTION
                                    </Typography>
                                    <XTextAreaInput
                                      name="description"
                                      label=""
                                      rows={2}
                                      variant='outlined'
                                    />
                                  </Grid>
                                </Grid>

                                <Grid item
                                  xs={12}
                                  sx={{
                                    my: 2
                                  }}
                                >
                                  <Typography variant="h6">IDENTIFICATION DOCUMENTS</Typography>
                                  <Typography variant="subtitle1">Select the identification documents you
                                    would like to capture from your customers</Typography>
                                  <TemplateTable
                                    columns={idDocumentsTable.columns}
                                    rows={idDocumentsTable.rows}
                                    values={values}
                                    onCheckboxChange={handleCheckboxChange}
                                  />

                                </Grid>

                                <Grid item
                                  xs={12}
                                  sx={{
                                    my: 2
                                  }}
                                >
                                  <Typography variant="h6">BIOMETRICS</Typography>
                                  <Typography variant="subtitle1">Select the biometric information you would
                                    like to capture from your customers</Typography>
                                  <TemplateTable
                                    columns={biometricsTable.columns}
                                    rows={biometricsTable.rows}
                                    values={values}
                                    onCheckboxChange={handleCheckboxChange}
                                  />
                                </Grid>

                                <Grid item
                                  xs={12}
                                  sx={{
                                    my: 2
                                  }}
                                >
                                  <Typography variant="h6">CONTACT INFORMATION</Typography>
                                  <Typography variant="subtitle1">Select the contact information you would
                                    like to capture from your customers</Typography>
                                  <TemplateTable
                                    columns={contactsTable.columns}
                                    rows={contactsTable.rows}
                                    values={values}
                                    onCheckboxChange={handleCheckboxChange}
                                  />
                                </Grid>

                                <Grid item
                                  xs={12}
                                  sx={{
                                    my: 2
                                  }}
                                >
                                  <Typography variant="h6">FINANCIAL INFORMATION</Typography>
                                  <Typography variant="subtitle1">Select the financial information you would
                                    like to capture from your customers</Typography>
                                  <TemplateTable
                                    columns={financialTable.columns}
                                    rows={financialTable.rows}
                                    values={values}
                                    onCheckboxChange={handleCheckboxChange}
                                  />
                                </Grid>

                                <Grid item
                                  xs={12}
                                  sx={{
                                    my: 2
                                  }}
                                >
                                  <Typography variant="h6">CUSTOMER INVITATION EMAIL</Typography>
                                  <Typography variant="subtitle1">Set a default email that will be sent to
                                    invite your customers to start the onboarding journey</Typography>
                                  <Grid item
                                    xs={12}
                                    sx={{ mt: 2 }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      SUBJECT LINE
                                    </Typography>

                                    <XTextInput
                                      name="invitationEmailSubject"
                                      label=""
                                      variant='outlined'
                                    />
                                  </Grid>
                                  <Grid item
                                    xs={12}
                                    sx={{ mt: 2 }}
                                  >

                                    <Typography variant="h6">EMAIL BODY</Typography>

                                    <XTextAreaInput
                                      name="invitationEmailBody"
                                      label=""
                                      variant='outlined'
                                    />
                                  </Grid>
                                </Grid>

                                <Grid item
                                  xs={12}
                                  sx={{
                                    my: 2
                                  }}
                                >
                                  <Typography variant="h6">COMPLETION EMAIL</Typography>
                                  <Typography variant="subtitle1">Set a default email that will be sent to
                                    your customers after completing the onboarding journey</Typography>
                                  <Grid item
                                    xs={12}
                                    sx={{ mt: 2 }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      SUBJECT LINE
                                    </Typography>
                                    <XTextInput
                                      name="completionEmailSubject"
                                      label=""
                                      variant='outlined'
                                    />
                                  </Grid>
                                  <Grid item
                                    xs={12}
                                    sx={{ mt: 2 }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      EMAIL BODY
                                    </Typography>
                                    <XTextAreaInput
                                      name="completionEmailBody"
                                      label=""
                                      variant='outlined'
                                    />
                                  </Grid>
                                </Grid>

                                <Grid item
                                  xs={12}
                                  sx={{
                                    my: 2
                                  }}
                                >
                                  <Typography variant="h6">CUSTOMER INVITATION SMS</Typography>
                                  <Typography variant="subtitle1">Set a default SMS that will be sent to
                                    invite your customers to start the onboarding journey</Typography>
                                  <Alert severity="info">
                                    In order to send SMS, you need to have SMS credits in our &nbsp;
                                    <span>
                                      <Link
                                        color="primary"
                                        href="https://messaging.iotec.io"
                                        target="_blank"
                                        rel="noopener"
                                      >
                                        Messaging app.
                                      </Link>
                                    </span>
                                  </Alert>
                                  <Grid item
                                    xs={12}
                                    sx={{ mt: 2 }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      SMS BODY
                                    </Typography>
                                    <XTextInput
                                      name="invitationSms"
                                      label=""
                                      variant="outlined"
                                      maxLength={160}
                                    />
                                  </Grid>
                                </Grid>

                                <Grid item
                                  xs={12}
                                  sx={{
                                    my: 2
                                  }}
                                >
                                  <Typography variant="h6">COMPLETION SMS</Typography>
                                  <Typography variant="subtitle1">Set a default SMS that will be sent to your
                                    customers after completing the onboarding journey</Typography>
                                  <Alert severity="info">
                                    In order to send SMS, you need to have SMS credits in our &nbsp;
                                    <span>
                                      <Link
                                        color="primary"
                                        href="https://messaging.iotec.io"
                                        target="_blank"
                                        rel="noopener"
                                      >
                                        Messaging app.
                                      </Link>
                                    </span>
                                  </Alert>
                                  <Grid item
                                    xs={12}
                                    sx={{ mt: 2 }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      SMS BODY
                                    </Typography>
                                    <XTextInput
                                      name="completionSms"
                                      label=""
                                      variant="outlined"
                                      maxLength={160}
                                    />
                                  </Grid>
                                </Grid>

                                <Grid item
                                  xs={12}
                                  sx={{ mt: 0 }}
                                >
                                  <Box display="flex"
                                    justifyContent="flex-end"
                                  >
                                    <LoadingButton
                                      variant="contained"
                                      color="primary"
                                      onClick={submitForm}
                                      loading={isSubmitting}
                                      disabled={isSubmitting}
                                    >
                                      Save changes
                                    </LoadingButton>
                                  </Box>
                                </Grid>

                              </Grid>

                            </Form>
                          </Box>
                        );
                      }}
                    </Formik>

                  </Box>
                </CardContent>
              </Card>
            </Grid>


          </ListViewLayout>
        </Box>
      </Grid>
      <Grid item
        xs={12} >
        <Box height="100%"
          ml={9}
          mr={9}
          mt={0}
          mb={5}
        >
          {
            data && (
              <Grid item
                xs={12}>
                {isEditMode && (
                  <ActivationManagement
                    id={data.id}
                    isActive={data?.isActive}
                    isLoading={!isEditMode}
                    url={remoteRoutes.peopleOnboardingTemplatesStatusChange}
                    entity="template"
                    warning={data?.isActive ? deactivationWarning : activationWarning}
                    onDone={handleRefresh}
                  />
                )}
              </Grid>
            )
          }
        </Box>
      </Grid>
    </Grid>

  )
  ;
};

const PeopleOnboardingTemplateLayout = () => {
  const params = useParams();
  const templateId = params?.templateId;
  const hasTemplateId = hasValue(templateId);
  const templateDetailsKey = ['people-onboarding-template', templateId];
  const { isLoading, error, data } = useQuery<OnboardingTemplateViewModel>(
    templateDetailsKey,
    () => getData(`${remoteRoutes.OnboardingTemplates}/${templateId}`),
    { enabled: hasTemplateId }
  );

  const {
    isLoading: onBoardingSettingsIsLoading,
    error: onBoardingSettingsError,
    data: onBoardingBaseCharge
  } = useQuery<number>(
    'baseChargeKey',
    () => getData(remoteRoutes.peopleOnboardingTemplatesBaseCharge)
  );

  if (isLoading || onBoardingSettingsIsLoading) return <Loading />;
  if ((!data) && hasTemplateId) return <ErrorComponent text={JSON.stringify(error)} />;
  if ((!onBoardingBaseCharge)) return <ErrorComponent text={JSON.stringify(onBoardingSettingsError)} />;

  return <PeopleTemplateForm
    data={data}
    cacheKey={templateDetailsKey}
    baseCharge={onBoardingBaseCharge}
  />;
};

export default PeopleOnboardingTemplateLayout;
